/**
 * Abas do Brain (AB#86817): Mind, Memory (AB#87149) e Knowledge, e dentro da Knowledge as abas que a
 * base de conhecimento já tinha. O que limpa o Brain inteiro (Security) não é aba:
 * fica nas configurações do Brain, na engrenagem ao lado do título, só para admin
 * (AB#87121). A Soul também não é aba: ela é a descrição do workspace e vive na aba
 * Geral das configurações dele. Os links salvos seguem valendo:
 * `overview` era a aba dos blocos, que virou a Mind; `files`, `pipelines` e
 * `embeddings` abrem a Knowledge já na sub-aba; `storage` era o nome da `files`
 * (AB#87121) e abre ela; `security` abre a Mind com as configurações abertas (ver
 * `isLegacySecurityTab`); `soul` vai para as configurações do workspace (ver
 * `isLegacySoulTab`).
 */

export type BrainTab = 'mind' | 'memory' | 'knowledge';
export type KnowledgeTab = 'files' | 'pipelines' | 'embeddings';

export const KNOWLEDGE_TABS: readonly KnowledgeTab[] = ['files', 'pipelines', 'embeddings'];

export interface BrainLocation {
  tab: BrainTab;
  knowledgeTab: KnowledgeTab;
}

export const DEFAULT_KNOWLEDGE_TAB: KnowledgeTab = 'files';

/** `?settings=` que abre as configurações do Brain. */
export const BRAIN_SETTINGS_PARAM = 'settings';
export const BRAIN_SETTINGS_OPEN = 'brain';

const LEGACY_SOUL_TAB = 'soul';
const LEGACY_FILES_TAB = 'storage';
const LEGACY_SECURITY_TAB = 'security';

function normalize(param: string | null | undefined): string {
  return (param ?? '').trim().toLowerCase();
}

/** Link salvo da antiga aba Soul do Brain: deve abrir as configurações do workspace. */
export function isLegacySoulTab(param: string | null | undefined): boolean {
  return normalize(param) === LEGACY_SOUL_TAB;
}

/** Link salvo da antiga aba Security: abre a Mind com as configurações do Brain abertas. */
export function isLegacySecurityTab(param: string | null | undefined): boolean {
  return normalize(param) === LEGACY_SECURITY_TAB;
}

/** Para onde vai um link salvo `?tab=soul`: a aba Geral (a padrão) das configurações do workspace. */
export function workspaceSoulPath(workspaceId: string): string {
  return `/products/${workspaceId}/config`;
}

export function resolveBrainTab(param: string | null | undefined): BrainLocation {
  const value = normalize(param);
  // A Memory do workspace (AB#87149), entre a Mind e a Knowledge, como na precedência.
  if (value === 'memory') return { tab: 'memory', knowledgeTab: DEFAULT_KNOWLEDGE_TAB };
  if (value === 'knowledge') return { tab: 'knowledge', knowledgeTab: DEFAULT_KNOWLEDGE_TAB };
  if (value === LEGACY_FILES_TAB) return { tab: 'knowledge', knowledgeTab: 'files' };
  if ((KNOWLEDGE_TABS as readonly string[]).includes(value)) {
    return { tab: 'knowledge', knowledgeTab: value as KnowledgeTab };
  }
  // `mind`, `overview`, `security`, `soul` sem workspace e qualquer valor desconhecido abrem a Mind.
  return { tab: 'mind', knowledgeTab: DEFAULT_KNOWLEDGE_TAB };
}

/** O valor de `?tab=` que leva de volta a esta posição. */
export function brainTabParam(location: BrainLocation): string {
  return location.tab === 'knowledge' ? location.knowledgeTab : location.tab;
}
